import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Container, Modal, SpaceBetween } from '@amzn/awsui-components-react/polaris';
import ErrorUtils from 'src/utils/ErrorUtils';
import { ColumnSourceType } from 'src/models/segment-cdt/SegmentCdtTypes';
import { SegmentTablesContext } from 'src/components/segment-cdt/SegmentTablesContext';
import { ACTIONS } from 'src/services/segment-cdt/SegmentTablesReducer';
import ColumnTypeChooser, {
    ColumnTypeOption, GL_PULL_TYPE, MANUAL_TYPE, DERIVED_PCT_TYPE, DERIVED_BAL_TYPE,
} from 'src/components/segment-cdt/create/ColumnTypeChooser';
import GlPullColumnForm from 'src/components/segment-cdt/create/GlPullColumnForm';
import ManualColumnForm from 'src/components/segment-cdt/create/ManualColumnForm';
import DerivedColumnForm from 'src/components/segment-cdt/create/DerivedColumnForm';
import { columnTypeLabel, periodBehaviorSummary} from 'src/components/segment-cdt/create/segmentCreateUtils';

/**
 * "Add columns" modal for an existing segment table. Reuses the create-wizard column editor end to
 * end: the type chooser (GL / Derived / Manual, with the two derived kinds nested under one card),
 * the GL / Manual / Derived forms, and the
 * reducer's columnEditor + queued-columns slice (unused on the detail route otherwise). On open it
 * starts a fresh add session seeded with the table's segment; queued columns are committed together
 * via AddSegmentColumns, whose response (the full effective column set) is pushed to the definition
 * so the grid re-renders. Reads everything from context; only visibility is a prop.
 */
export default function SegmentAddColumnModal(props: {
    visible: boolean;
    onDismiss: () => void;
}) {
    const { visible, onDismiss } = props;
    // Render nothing while closed so the reused forms' LOV fetches and every hook run only while open.
    if (!visible) {
        return null;
    }
    return <AddColumnModalBody onDismiss={onDismiss} />;
}

function AddColumnModalBody(props: { onDismiss: () => void }) {
    const { onDismiss } = props;
    const { state, dispatch, services } = useContext(SegmentTablesContext);
    const cw = state.createWizard;
    const editor = cw.columnEditor;
    const choosing = cw.choosing;
    const definition = state.detail.definition;
    const tableId = state.detail.tableId ?? '';
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    // The "Derived Column" card opens a second-level chooser (% of total / derived balance). Local
    // because it is transient sub-menu navigation, not feature state.
    const [derivedFlavorOpen, setDerivedFlavorOpen] = useState(false);
    // Bumped on dismiss/unmount so a still-in-flight save abandons instead of dispatching into a closed
    // or unmounted modal.
    const saveSeq = useRef(0);

    // Start a clean add session, seeded with the table's segment, when the modal opens.
    useEffect(() => {
        dispatch(ACTIONS.START_ADD_COLUMNS_SESSION.withPayload({ segmentType: definition?.segmentType }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => () => { saveSeq.current++; }, []);

    const queued = cw.columns;

    // When a column is committed (queue grows), return to the list. A Back from a form also clears the
    // editor but does NOT grow the queue, so it can navigate to the chooser/flavor instead.
    // useLayoutEffect, not useEffect: COMMIT_COLUMN clears columnEditor but leaves choosing /
    // derivedFlavorOpen set, so a post-paint reset renders one frame where the editor is gone but the
    // chooser is still selected — a visible flash of the chooser before the queued list appears.
    // Resetting before paint removes it.
    const prevLen = useRef(queued.length);
    useLayoutEffect(() => {
        if (queued.length > prevLen.current) {
            setDerivedFlavorOpen(false);
            if (cw.choosing) dispatch(ACTIONS.SET_CHOOSING.withPayload(false));
        }
        // A rejected save names a specific queued column, so the banner is stale the moment the queue
        // changes — clear it rather than leaving a failure showing against columns the user has since
        // edited, which reads as though the new attempt failed too.
        if (queued.length !== prevLen.current) {
            setSaveError('');
        }
        prevLen.current = queued.length;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queued.length]);

    // Reset the editor slice to a clean, segment-seeded session. Called on close so a reopen never
    // paints the previous session's queued columns / open form for a frame (and never remounts a form
    // just to re-clear it). The open-time mount effect resets too, as a belt-and-braces guard.
    const resetSession = () => dispatch(ACTIONS.START_ADD_COLUMNS_SESSION.withPayload({ segmentType: definition?.segmentType }));

    // Remove a queued column, plus any queued column whose derived reference points at it: otherwise
    // that reference dangles and the backend rejects the whole add naming a column no longer shown.
    //
    // Single-hop derivation means a derived column only references non-derived columns, so one level of
    // cascade is sufficient — no transitive dangles are possible. (Were chained derivation ever allowed,
    // C -> B -> A, this would need to recurse: removing A takes B but would leave C dangling.)
    const onRemoveQueued = (idx: number) => {
        const removedId = queued[idx]?.columnId;
        const indices = queued
            .map((c, i) => ({ c, i }))
            .filter(({ c, i }) => i === idx || (!!removedId && !!c.derivedConfig
                && [c.derivedConfig.pctColumnId, c.derivedConfig.valueColumnId, c.derivedConfig.sourceColumnId].indexOf(removedId) !== -1))
            .map((x) => x.i)
            .sort((a, b) => b - a); // remove high-to-low so the remaining indices stay valid
        indices.forEach((i) => dispatch(ACTIONS.REMOVE_COLUMN.withPayload(i)));
    };

    /*
     * The `saving` guard plus `disabled={saving}` on the Save button is what prevents a duplicate
     * AddSegmentColumns POST: React flushes the state update before the second click can be handled, so
     * two in-flight adds are not reachable from the UI. `saveSeq` is a separate concern — it protects
     * state coherence (only the latest save dispatches SET_DETAIL_DEFINITION / REFRESH), not the request.
     * Worth knowing that the server would not absorb a duplicate: AddSegmentColumns is deliberately not
     * @idempotent, so a genuine replay is rejected rather than silently reported as a no-op success.
     *
     * Two consequences of dismiss-being-allowed mid-save (see handleDismiss), both accepted knowingly as the
     * better side of the trade against trapping the user in a hung modal:
     *  - A save that SUCCEEDS after dismissal lands invisibly. The seq guard skips SET_DETAIL_DEFINITION,
     *    REFRESH_DETAIL_ROWS and the success banner, so the columns exist server-side but the grid will not
     *    show them until the next definition fetch (reopen / reload).
     *  - Because the operation is not idempotent, retrying such an add is REJECTED by the server even though
     *    the first attempt landed, and the UI cannot tell that apart from a genuine failure. The recovery is
     *    to reload and look at the column set before retrying.
     */
    const onSave = async () => {
        if (saving || queued.length === 0 || !tableId) {
            return;
        }
        const seq = ++saveSeq.current;
        setSaving(true);
        setSaveError('');
        try {
            const resp = await services.segmentDataTablesService.addSegmentColumns(tableId, queued);
            if (seq !== saveSeq.current) return; // dismissed / unmounted mid-save
            // The response echoes the full effective column set; push it to the definition so the grid
            // re-renders from context, then refresh the rows (new columns are blank until pulled/uploaded).
            if (definition && resp?.columns) {
                dispatch(ACTIONS.SET_DETAIL_DEFINITION.withPayload({ ...definition, columns: resp.columns }));
            }
            dispatch(ACTIONS.REFRESH_DETAIL_ROWS);
            services.messageService.showSuccessAutoDismissBanner(
                queued.length === 1 ? '1 column added.' : `${queued.length} columns added.`,
            );
            resetSession();
            onDismiss();
        } catch (ex) {
            if (seq === saveSeq.current) setSaveError(ErrorUtils.getMessage(ex) || 'Could not add columns.');
        } finally {
            if (seq === saveSeq.current) setSaving(false);
        }
    };

    // Dismiss is always available, including mid-save. A dropped socket leaves `saving` true forever
    // (fetch has no default timeout), and refusing to close would strand the user with every exit dead
    // until a page reload. Bumping saveSeq first makes that safe: a late response sees seq !== saveSeq
    // and drops silently rather than dispatching into a closed modal.
    const handleDismiss = () => {
        saveSeq.current++;
        resetSession();
        onDismiss();
    };

    // Views, outermost first: an open editor (form) > the derived-flavor sub-chooser > the type
    // chooser > the queued-columns list.
    const view: 'form' | 'flavor' | 'chooser' | 'list' =
        editor ? 'form' : derivedFlavorOpen ? 'flavor' : choosing ? 'chooser' : 'list';

    // One "Back" that steps out one level: form -> its chooser, flavor -> top chooser, chooser -> list.
    const onBack = () => {
        if (editor) {
            const st = editor.draft.sourceType;
            const derived = st === ColumnSourceType.DERIVED_PCT || st === ColumnSourceType.DERIVED_BAL;
            dispatch(ACTIONS.CANCEL_COLUMN_EDITOR);
            setDerivedFlavorOpen(derived); // a derived form returns to the flavor sub-chooser
            dispatch(ACTIONS.SET_CHOOSING.withPayload(true));
        } else if (derivedFlavorOpen) {
            setDerivedFlavorOpen(false);
        } else if (choosing) {
            dispatch(ACTIONS.SET_CHOOSING.withPayload(false));
        }
    };

    // Top-level chooser: GL, a single "Derived Column" card that opens the flavor sub-chooser, Manual.
    const topTypes: ColumnTypeOption[] = [
        GL_PULL_TYPE,
        {
            key: 'DERIVED',
            icon: 'segment-derived.svg',
            title: 'Derived Column',
            description: 'Computed from other columns: a % of a column total, or a % applied to an aggregated GL balance pull.',
            onSelect: () => setDerivedFlavorOpen(true),
        },
        MANUAL_TYPE,
    ];

    const renderForm = () => {
        const st = editor?.draft.sourceType;
        if (st === ColumnSourceType.PULL_GL) return <GlPullColumnForm hideCancel />;
        if (st === ColumnSourceType.MANUAL) return <ManualColumnForm hideCancel />;
        return <DerivedColumnForm hideCancel />;
    };

    return (
        <Modal
            visible
            onDismiss={handleDismiss}
            size="large"
            closeAriaLabel="Close add columns dialog"
            header={definition?.tableName ? `Add columns: ${definition.tableName}` : 'Add columns'}
            footer={
                <Box float="right">
                    {/* Back (only inside a sub-menu) + Close, side by side; Save is the list-view primary. */}
                    <SpaceBetween direction="horizontal" size="xs">
                        {view !== 'list' && <Button disabled={saving} onClick={onBack}>Back</Button>}
                        {/* Not disabled while saving: a hung request must stay escapable (see handleDismiss). */}
                        <Button variant="link" onClick={handleDismiss}>Close</Button>
                        {view === 'list' && (
                            <Button
                                variant="primary"
                                loading={saving}
                                disabled={saving || queued.length === 0}
                                onClick={onSave}
                            >
                                {queued.length <= 1 ? 'Save column' : `Save ${queued.length} columns`}
                            </Button>
                        )}
                    </SpaceBetween>
                </Box>
            }
        >
            <SpaceBetween size="m">
                {view === 'list' && queued.length > 0 && (
                    <Container>
                        <Box variant="h4" margin={{ bottom: 'xs' }}>Columns to add ({queued.length})</Box>
                        <SpaceBetween size="xs">
                            {queued.map((c, i) => (
                                <div key={c.columnId} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                                    <SpaceBetween size="s" direction="horizontal" alignItems="center">
                                        <Box variant="strong">{i + 1}. {c.displayName}</Box>
                                        {/* Includes the period behaviour: columns are add-only, so this list is
                                            the last place a mis-picked one can be spotted and removed. */}
                                        <Box variant="small" color="text-body-secondary">
                                            {[columnTypeLabel(c.sourceType), periodBehaviorSummary(c)]
                                                .filter((part) => !!part).join(' · ')}
                                        </Box>
                                    </SpaceBetween>
                                    <Button variant="link" disabled={saving} onClick={() => onRemoveQueued(i)}>Remove</Button>
                                </div>
                            ))}
                        </SpaceBetween>
                    </Container>
                )}

                {view === 'list' && queued.length === 0 && (
                    <Box color="text-body-secondary">No columns added yet. Use "Add column" below to add one.</Box>
                )}

                {view === 'list' && (
                    <Button iconName="add-plus" onClick={() => dispatch(ACTIONS.SET_CHOOSING.withPayload(true))}>
                        Add column
                    </Button>
                )}

                {view === 'chooser' && (
                    <ColumnTypeChooser
                        types={topTypes}
                        onPick={(sourceType) => dispatch(ACTIONS.START_COLUMN_EDITOR.withPayload({ sourceType }))}
                    />
                )}

                {view === 'flavor' && (
                    <ColumnTypeChooser
                        heading="Select derived column type"
                        types={[DERIVED_PCT_TYPE, DERIVED_BAL_TYPE]}
                        onPick={(sourceType) => dispatch(ACTIONS.START_COLUMN_EDITOR.withPayload({ sourceType }))}
                    />
                )}

                {view === 'form' && renderForm()}

                {saveError && <Alert type="error" header="Could not add columns">{saveError}</Alert>}
            </SpaceBetween>
        </Modal>
    );
}
